import { useEffect, useState, type DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { line, Size, partsStageRefused, boardColumns, boardFits, columnTotal, dropTarget, formatDateOnly, initialsOf, matchesCriteria, stalePillText, t, type BoardCard, type CriteriaRow, type DealStage } from '@bms2/shared';
import { useDateFormat } from '../../shell/ViewerZone';
import { Pill } from '../../components/controls';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { useApi } from './api';
import styles from './DealPage.module.css';
import { Money } from '../../components/Money';

export const BOARD_PATH = '/deals/board';

/**
 * The Deals list's board rendering (piece 12; decision 0206): one column per
 * top-level stage of the seeded set, cards showing value, owner, days in
 * stage and the STALE pill. 🔨 Drag never advances a stage: a card dropped in
 * another column opens the Deal at that gate and moves nothing, so the
 * refusal teaches rather than blocks.
 */
export function PipelineBoard({ apiBaseUrl = '', term = '', criteria = [] }: { apiBaseUrl?: string; term?: string; /** The list's current view criteria, applied to the cards the way the list applies them to its rows, so the two shapes agree (piece 12). */ criteria?: readonly CriteriaRow[] }) {
  const navigate = useNavigate();
  const { state, reload } = useApi<{ cards: BoardCard[] }>(`${BOARD_PATH}${term ? `?q=${encodeURIComponent(term)}` : ''}`, apiBaseUrl);
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<DealStage | null>(null);
  /** 🔒 Why the last drop opened nothing (decision 2430; T1722): a Parts page's Deal dropped on Negotiation or Decision. */
  const [refused, setRefused] = useState<string | null>(null);
  const dateFormat = useDateFormat();
  // 🔨 The board never scrolls sideways: it measures its own width and stacks the columns when they would not all fit (decision 0327).
  const [boardEl, setBoardEl] = useState<HTMLDivElement | null>(null);
  const [boardWidth, setBoardWidth] = useState<number | null>(null);
  useEffect(() => {
    if (!boardEl || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => setBoardWidth(entries[0]?.contentRect.width ?? null));
    observer.observe(boardEl);
    return () => observer.disconnect();
  }, [boardEl]);

  const drop = (stage: DealStage) => (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    // jsdom and some browsers hand a drop no dataTransfer; the card dragged is remembered either way.
    const id = event.dataTransfer?.getData('text/deal-id') || dragging;
    setOver(null);
    setDragging(null);
    if (!id) return;
    const card = state.kind === 'ready' ? state.data.cards.find((c) => c.id === id) : undefined;
    if (card?.dealPage && partsStageRefused(card.dealPage, stage)) return setRefused(t('deal.board.partsRefused'));
    setRefused(null);
    navigate(dropTarget(id, stage));
  };

  return (
    <AsyncStatePanel state={state} emptyAction={t('deal.board.emptyAction')} onRetry={reload}>
      {({ cards: all }) => {
        // 🔨 The board reads the same SavedView filters as the list (piece 12): a card the view's criteria exclude is not on the board.
        const answer = { cards: criteria.length > 0 ? all.filter((card) => matchesCriteria(card as unknown as Record<string, unknown>, criteria)) : all };
        return (
        <div data-pipeline-board="true">
          <p className={styles.boardHint}>{t('deal.board.dropHint')}</p>
          {refused ? <p className={styles.boardHint} role="status" data-board-refused="true">{refused}</p> : null}
          <div ref={setBoardEl} className={styles.board} data-board-layout={boardWidth !== null && boardFits(boardWidth, boardColumns(answer.cards).length) ? 'row' : 'stack'}>
            {boardColumns(answer.cards).map((column) => (
              <section
                key={column.stage}
                className={over === column.stage ? styles.columnOver : styles.column}
                data-board-column={column.stage}
                data-board-count={column.cards.length}
                onDragOver={(event) => {
                  event.preventDefault();
                  setOver(column.stage);
                }}
                onDragLeave={() => setOver(null)}
                onDrop={drop(column.stage)}
              >
                <h2 className={styles.columnTitle}>
                  <span>{line(column.stage, `· ${column.cards.length}`)}</span>
                  <span className={styles.columnTotal} data-board-total={column.stage}><Money cents={columnTotal(column.cards)} /></span>
                </h2>
                <ul className={styles.cards}>
                  {column.cards.length === 0 ? <li className={styles.columnEmpty}>{t('deal.board.empty')}</li> : null}
                  {column.cards.map((card) => (
                    <li
                      key={card.id}
                      className={styles.boardCard}
                      data-board-card={card.id}
                      draggable
                      onDragStart={(event) => {
                        event.dataTransfer?.setData('text/deal-id', card.id);
                        setDragging(card.id);
                      }}
                      onClick={() => navigate(`/deals/${card.id}`)}
                    >
                      {/* The card reads as the deal's preview: name and account, the value with its type, the owner, the expected close and the days in stage (decision 0300). */}
                      <span className={styles.boardCardName}>{card.name}</span>
                      <span className={styles.boardCardDetail}>{card.customerName}</span>
                      <span className={styles.boardCardRow}>
                        <span className={styles.boardCardValue}><Money cents={card.dealValueCents} /></span>
                        {card.dealType ? <Pill text={card.dealType} foreground="var(--status-inactive-text)" background="var(--status-inactive-bg)" size={Size.Small} /> : null}
                      </span>
                      <span className={styles.boardCardOwner}>
                        <span className={styles.boardCardAvatar} aria-hidden="true">{initialsOf(card.ownerName)}</span>
                        <span className={styles.boardCardDetail}>{card.ownerName}</span>
                        {card.expectedCloseDate ? <span className={styles.boardCardClose} data-board-close={card.expectedCloseDate}>{line(t('deal.board.closes'), formatDateOnly(card.expectedCloseDate, dateFormat))}</span> : null}
                      </span>
                      <span className={styles.boardCardRow}>
                        <span className={styles.boardCardDetail}>{line(card.daysInStage, t('deal.board.days'))}</span>
                        {card.stale ? <Pill text={stalePillText(card.staleDays ?? card.daysInStage)} foreground="var(--status-error-text)" background="var(--status-error-bg)" size={Size.Small} /> : null}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </div>
        );
      }}
    </AsyncStatePanel>
  );
}
